import { useEffect, useRef } from "react"
import type { TranslateRow } from "../../api/types"
import { SHORT_NAME_KEY, SOURCE_NAME, symbols, UNIT_KEY } from "./rules"

export interface RowProps {
	row: TranslateRow
	value: string
	dirty: boolean
	/** Сколько строк набора уедут вместе с этой — бэк разносит перевод по фразе. */
	twins: number
	showSource: boolean
	showExtras: boolean
	showSymbols: boolean
	/** undefined — колонка выключена; null — ссылки для этой строки нет. */
	url: string | null | undefined
	extras: Record<string, string>
	editable: boolean
	canMachine: boolean
	picked: boolean
	onPick: () => void
	onChange: (value: string) => void
	onRevert: () => void
	onExtra: (field: string, value: string) => void
	onMachine: () => void
}

/** Строка справочника переводов. */
export default function DictionaryRow(props: RowProps) {
	const { row } = props
	const stored = row.additionalInfo ?? {}

	return (
		<tr className={props.dirty ? "dict-row--dirty" : undefined}>
			<td className="dict-table__pick">
				<input type="checkbox" checked={props.picked} onChange={props.onPick} />
			</td>
			<td className="dict-table__code">
				<div className="mono muted">{row.translateId}</div>
				{props.showSource && (
					<div className="dict-source">{SOURCE_NAME.get(row.source)}</div>
				)}
			</td>
			<td className="dict-russian">
				{row.russian || <span className="muted">пусто</span>}
				{props.twins > 1 && (
					<span
						className="dict-twins"
						title={`Такая же фраза в наборе встречается ${props.twins} раз — перевод сохранится во все`}
					>
						×{props.twins}
					</span>
				)}
			</td>
			<td className="dict-table__value">
				<AutoTextarea
					value={props.value}
					disabled={!props.editable}
					dirty={props.dirty}
					onChange={props.onChange}
					onRevert={props.onRevert}
				/>
			</td>
			{props.showExtras && (
				<>
					<td className="dict-table__extra">
						<input
							className="input"
							disabled={!props.editable}
							value={
								props.extras[`${row.sourceId}:${SHORT_NAME_KEY}`] ??
								stored[SHORT_NAME_KEY] ??
								""
							}
							onChange={event =>
								props.onExtra(SHORT_NAME_KEY, event.target.value)
							}
						/>
					</td>
					<td className="dict-table__extra">
						<input
							className="input"
							disabled={!props.editable}
							value={
								props.extras[`${row.sourceId}:${UNIT_KEY}`] ??
								stored[UNIT_KEY] ??
								""
							}
							onChange={event => props.onExtra(UNIT_KEY, event.target.value)}
						/>
					</td>
				</>
			)}
			{props.showSymbols && (
				<td className="dict-table__symbols mono muted">
					{symbols(row.russian).toLocaleString("ru")}
				</td>
			)}
			{props.url !== undefined && (
				<td className="dict-table__link">
					{props.url ? (
						<a href={`https://${props.url}`} target="_blank" rel="noreferrer">
							{props.url}
						</a>
					) : (
						<span className="muted">—</span>
					)}
				</td>
			)}
			<td className="dict-table__act">
				{props.canMachine && (
					<button
						className="btn dict-translate"
						disabled={!row.russian}
						title="Перевести эту строку машинно. Результат встанет в поле — сохранять всё равно вручную"
						onClick={props.onMachine}
					>
						<TranslateIcon />
						Перевести
					</button>
				)}
			</td>
		</tr>
	)
}

/** Значок машинного перевода — тот же смысл, что у иконки «translate»
 *  в старом фронте (`library/icons`), но без подключения спрайта. */
export function TranslateIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			width="14"
			height="14"
			fill="currentColor"
			aria-hidden="true"
		>
			<path d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" />
		</svg>
	)
}

/**
 * Поле перевода. Растёт под текст: в справочнике рядом стоят и названия групп
 * в три слова, и описания на абзац. Старый фронт держал у всех строк
 * `rows={7}`, из-за чего на экран помещалось четыре строки таблицы.
 */
function AutoTextarea(props: {
	value: string
	disabled: boolean
	dirty: boolean
	onChange: (value: string) => void
	onRevert: () => void
}) {
	const field = useRef<HTMLTextAreaElement>(null)

	useEffect(() => {
		const node = field.current
		if (!node) return
		node.style.height = "auto"
		node.style.height = Math.min(node.scrollHeight, 220) + "px"
	}, [props.value])

	return (
		<textarea
			ref={field}
			className={
				"textarea dict-value" + (props.dirty ? " dict-value--dirty" : "")
			}
			rows={1}
			value={props.value}
			disabled={props.disabled}
			data-dict-value="1"
			onChange={event => props.onChange(event.target.value)}
			onKeyDown={event => {
				if (event.key === "Escape") {
					event.preventDefault()
					props.onRevert()
					event.currentTarget.blur()
					return
				}
				// Enter — вниз по столбцу, как в таблице; перенос строки — Shift+Enter
				if (event.key === "Enter" && !event.shiftKey) {
					event.preventDefault()
					const fields = Array.from(
						document.querySelectorAll<HTMLTextAreaElement>("[data-dict-value]")
					)
					const at = fields.indexOf(event.currentTarget)
					fields[at + 1]?.focus()
				}
			}}
		/>
	)
}
